import React, { useState, useEffect } from 'react';
import { Modal, Table, Button, message, Empty, Input } from 'antd';
import { DeleteOutlined, PlusOutlined, CloseOutlined } from '@ant-design/icons';
// eslint-disable-next-line import/no-extraneous-dependencies
import dayjs from 'dayjs';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { Box, Flex, Text } from 'atoms';
import { API } from 'api';

const { TextArea } = Input;

const StyledModal = styled(Modal)`
	.ant-modal-header {
		border-bottom: none;
		padding: 20px 24px;
		background: #fff;
	}

	.ant-modal-title {
		font-size: 16px;
		font-weight: 600;
		color: #000;
	}

	.ant-modal-body {
		padding: 0 24px 24px;
		max-height: 600px;
		overflow-y: auto;
	}

	.ant-modal-footer {
		display: none;
	}

	.ant-modal-close {
		top: 16px;
		right: 16px;
	}
`;

const ModalHeader = styled(Flex)`
	padding: 20px 24px;
	border-bottom: 1px solid #e8e8e8;
	justify-content: space-between;
	align-items: flex-start;
	background: #fff;
`;

const HeaderContent = styled(Box)`
	flex: 1;
`;

const RouteTitle = styled(Text)`
	font-size: 16px;
	font-weight: 600;
	color: #000;
	margin-bottom: 4px;
	display: block;
`;

const ProjectTitle = styled(Text)`
	font-size: 14px;
	color: #666;
	display: block;
`;

const CloseButton = styled(Button)`
	border: none;
	background: transparent;
	box-shadow: none;
	padding: 0;
	width: 32px;
	height: 32px;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 20px;
	color: #999;
	cursor: pointer;

	&:hover {
		color: #000 !important;
		background: transparent !important;
		border: none !important;
	}
`;

const DeleteButton = styled(Button)`
	background: #ff4d4f;
	border-color: #ff4d4f;
	color: #fff;
	height: 40px;
	padding: 0 24px;
	font-weight: 500;

	&:hover {
		background: #ff7875 !important;
		border-color: #ff7875 !important;
		color: #fff !important;
	}

	&:disabled {
		background: #f5f5f5;
		border-color: #d9d9d9;
		color: #bfbfbf;
	}
`;

const GenerateButton = styled(Button)`
	background: #18405d;
	border-color: #18405d;
	color: #fff;
	height: 40px;
	padding: 0 24px;
	font-weight: 500;
	display: flex;
	align-items: center;
	gap: 8px;

	&:hover {
		background: #1e4a6b !important;
		border-color: #1e4a6b !important;
		color: #fff !important;
	}
`;

const EmptyStateContainer = styled(Box)`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 60px 20px;
`;

const EmptyTitle = styled(Text)`
	font-size: 18px;
	font-weight: 600;
	color: #333;
	margin-bottom: 8px;
`;

const EmptyReason = styled(Text)`
	font-size: 14px;
	color: #666;
	margin-bottom: 24px;
`;

const StyledTable = styled(Table)`
	margin-top: 24px;

	.ant-table {
		border: 1px solid #e8e8e8;
		border-radius: 0;
	}

	.ant-table-thead > tr > th {
		background: #18405d;
		color: #fff;
		font-weight: 600;
		font-size: 13px;
		border-bottom: none;
		padding: 12px 16px;
		border-right: 1px solid rgba(255, 255, 255, 0.1);

		&:last-child {
			border-right: none;
		}
	}

	.ant-table-tbody > tr > td {
		padding: 12px 16px;
		border-bottom: 1px solid #f0f0f0;
		font-size: 13px;
	}

	.ant-table-tbody > tr:hover > td {
		background: #fafafa;
	}

	.ant-checkbox-wrapper {
		.ant-checkbox-inner {
			border-radius: 2px;
		}
	}
`;

const ConfirmModal = styled(Modal)`
	.ant-modal-content {
		border-radius: 8px;
	}

	.ant-modal-header {
		border-bottom: none;
		padding: 24px 24px 16px;
	}

	.ant-modal-title {
		font-size: 18px;
		font-weight: 600;
		color: #000;
	}

	.ant-modal-body {
		padding: 0 24px 24px;
	}

	.ant-modal-footer {
		border-top: none;
		padding: 0 24px 24px;
		display: flex;
		justify-content: flex-end;
		gap: 12px;
	}
`;

const ConfirmDescription = styled(Text)`
	font-size: 14px;
	color: #666;
	display: block;
	margin-bottom: 16px;
`;

const ReasonLabel = styled(Text)`
	font-size: 14px;
	font-weight: 500;
	color: #333;
	display: block;
	margin-bottom: 8px;
`;

const CancelButtonConfirm = styled(Button)`
	height: 40px;
	padding: 0 24px;
	border: 1px solid #d9d9d9;
	color: #000;
	font-weight: 500;

	&:hover {
		border-color: #18405d !important;
		color: #18405d !important;
	}
`;

const ConfirmButtonAction = styled(Button)`
	height: 40px;
	padding: 0 24px;
	background: #18405d;
	border-color: #18405d;
	color: #fff;
	font-weight: 500;

	&:hover {
		background: #1e4a6b !important;
		border-color: #1e4a6b !important;
	}
`;

export default function ManageTripsModal({
	visible,
	onClose,
	routeData,
	onGenerateTrips,
	onDeleteTrips,
}) {
	const [trips, setTrips] = useState([]);
	const [loading, setLoading] = useState(false);
	const [selectedRowKeys, setSelectedRowKeys] = useState([]);
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
	const [showGenerateConfirm, setShowGenerateConfirm] = useState(false);
	const [deleteReason, setDeleteReason] = useState('');
	const [generateReason, setGenerateReason] = useState('');

	const routeId = routeData?.route_id || routeData?.id;
	const routeName = routeData?.route_name || '';
	const projectName = routeData?.project_name || routeData?.project_id;

	// Fetch trips when modal opens
	useEffect(() => {
		if (visible && routeId) {
			fetchTrips();
		} else if (!visible) {
			// Reset state when modal closes
			setTrips([]);
			setSelectedRowKeys([]);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [visible, routeId]);

	const fetchTrips = async () => {
		setLoading(true);
		try {
			const CREDENTIALS = {
				url: '/search',
				data: {
					search_type: 'trip',
					filter_by: {
						route_id: routeId,
					},
					pagination: {
						page: 1,
						per_page: 20,
					},
					sort_by: {
						field: 'created_at',
						order: 'desc',
					},
				},
				version: 'v2',
			};

			const response = await API.common(CREDENTIALS);

			// Handle V2 response structure: { data: { trips: [...], pagination: {...} }, status: "success" }
			if (response?.status === 'success' && response?.data?.trips) {
				setTrips(response.data.trips);
			} else if (response?.data?.rows) {
				// Fallback for alternative response structure
				setTrips(response.data.rows);
			} else {
				setTrips([]);
			}
		} catch (error) {
			console.error('Failed to fetch trips:', error);
			message.error(error?.message || 'Failed to load trips');
			setTrips([]);
		} finally {
			setLoading(false);
		}
	};

	const handleDeleteTripsClick = () => {
		if (selectedRowKeys.length === 0) {
			message.warning('Please select trips to delete');
			return;
		}
		setShowDeleteConfirm(true);
	};

	const handleGenerateTripsClick = () => {
		setShowGenerateConfirm(true);
	};

	const handleConfirmDelete = () => {
		if (onDeleteTrips) {
			onDeleteTrips(selectedRowKeys, deleteReason);
		}
		setShowDeleteConfirm(false);
		setDeleteReason('');
		setSelectedRowKeys([]);
	};

	const handleConfirmGenerate = async () => {
		if (onGenerateTrips) {
			try {
				await onGenerateTrips(generateReason);
				// Refresh trips list after successful generation
				await fetchTrips();
			} catch (error) {
				console.error('Error generating trips:', error);
			}
		}
		setShowGenerateConfirm(false);
		setGenerateReason('');
	};

	const handleCancelDelete = () => {
		setShowDeleteConfirm(false);
		setDeleteReason('');
	};

	const handleCancelGenerate = () => {
		setShowGenerateConfirm(false);
		setGenerateReason('');
	};

	const columns = [
		{
			title: 'Trip ID',
			dataIndex: 'id',
			key: 'id',
			width: 100,
		},
		{
			title: 'Vendor',
			dataIndex: 'vendor_name',
			key: 'vendor_name',
			render: (text, record) => text || record.vendor || '-',
		},
		{
			title: 'Vehicle No.',
			dataIndex: 'vehicle_no',
			key: 'vehicle_no',
			render: (text, record) => text || record.vehicle_number || '-',
		},
		{
			title: 'City',
			dataIndex: 'city',
			key: 'city',
			render: (text) => text || '-',
		},
		{
			title: 'Reporting time',
			dataIndex: 'reporting_time',
			key: 'reporting_time',
			render: (text, record) => {
				const time = text || record.reporting_time || record.start_time;
				return time ? dayjs(time).format('hh:mm A') : '-';
			},
		},
		{
			title: 'Created by',
			dataIndex: 'created_by_name',
			key: 'created_by_name',
			render: (text, record) => text || record.creator || '-',
		},
		{
			title: 'Created at',
			dataIndex: 'created_at',
			key: 'created_at',
			render: (text) => (text ? dayjs(text).format('DD-MM-YYYY') : '-'),
		},
		{
			title: 'Start Time/ Duration',
			dataIndex: 'duration',
			key: 'duration',
			render: (text, record) => {
				const startTime = record.start_time
					? dayjs(record.start_time).format('DD-MM-YYYY HH:mm')
					: '-';
				const duration = record.duration || text || '-';
				return `${startTime}\n${duration}`;
			},
		},
	];

	const rowSelection = {
		selectedRowKeys,
		onChange: setSelectedRowKeys,
	};

	return (
		<>
			<StyledModal
				title={null}
				open={visible}
				onCancel={onClose}
				footer={null}
				width={1200}
				centered
				maskClosable={false}
				closable={false}
			>
				<ModalHeader>
					<HeaderContent>
						<RouteTitle>Route Name : {routeName}</RouteTitle>
						<ProjectTitle>Project Name : {projectName}</ProjectTitle>
					</HeaderContent>
					<CloseButton onClick={onClose}>
						<CloseOutlined />
					</CloseButton>
				</ModalHeader>

				<Box style={{ padding: '24px' }}>
					{loading && (
						<Box style={{ textAlign: 'center', padding: '40px 0' }}>
							<Text>Loading trips...</Text>
						</Box>
					)}

					{!loading && trips.length === 0 && (
						<EmptyStateContainer>
							<Empty
								description={
									<>
										<EmptyTitle>No Trips Found</EmptyTitle>
										<EmptyReason>Reason: Day off</EmptyReason>
									</>
								}
							/>
							<GenerateButton
								icon={<PlusOutlined />}
								onClick={handleGenerateTripsClick}
							>
								Generate Trips
							</GenerateButton>
						</EmptyStateContainer>
					)}

					{!loading && trips.length > 0 && (
						<>
							<StyledTable
								rowSelection={rowSelection}
								columns={columns}
								dataSource={trips}
								rowKey="id"
								pagination={false}
								scroll={{ x: 'max-content' }}
							/>
							<Flex justify="flex-end" style={{ marginTop: 24 }}>
								<DeleteButton
									icon={<DeleteOutlined />}
									onClick={handleDeleteTripsClick}
									disabled={selectedRowKeys.length === 0}
								>
									Delete Trips
								</DeleteButton>
							</Flex>
						</>
					)}
				</Box>
			</StyledModal>

			{/* Delete Confirmation Modal */}
			<ConfirmModal
				title="Are you sure?"
				open={showDeleteConfirm}
				onCancel={handleCancelDelete}
				footer={[
					<CancelButtonConfirm key="cancel" onClick={handleCancelDelete}>
						Cancel
					</CancelButtonConfirm>,
					<ConfirmButtonAction key="confirm" onClick={handleConfirmDelete}>
						Confirm
					</ConfirmButtonAction>,
				]}
				width={400}
				centered
			>
				<ConfirmDescription>
					Deleting this Trips will remove all the trips auto generated for today. Please
					confirm to proceed.
				</ConfirmDescription>
				<ReasonLabel>Reason for Deleting</ReasonLabel>
				<TextArea
					rows={3}
					placeholder="Add your reasons"
					value={deleteReason}
					onChange={(e) => setDeleteReason(e.target.value)}
				/>
			</ConfirmModal>

			{/* Generate Confirmation Modal */}
			<ConfirmModal
				title="Are you sure?"
				open={showGenerateConfirm}
				onCancel={handleCancelGenerate}
				footer={[
					<CancelButtonConfirm key="cancel" onClick={handleCancelGenerate}>
						Cancel
					</CancelButtonConfirm>,
					<ConfirmButtonAction key="confirm" onClick={handleConfirmGenerate}>
						Confirm
					</ConfirmButtonAction>,
				]}
				width={400}
				centered
			>
				<ConfirmDescription>
					Generating Trips will Generate regular trips for today. Please confirm to
					proceed.
				</ConfirmDescription>
				<ReasonLabel>Reason for Generating</ReasonLabel>
				<TextArea
					rows={3}
					placeholder="Add your reasons"
					value={generateReason}
					onChange={(e) => setGenerateReason(e.target.value)}
				/>
			</ConfirmModal>
		</>
	);
}

ManageTripsModal.propTypes = {
	visible: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	routeData: PropTypes.shape({
		id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		route_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		route_name: PropTypes.string,
		project_name: PropTypes.string,
		project_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	}),
	onGenerateTrips: PropTypes.func,
	onDeleteTrips: PropTypes.func,
};

ManageTripsModal.defaultProps = {
	routeData: null,
	onGenerateTrips: null,
	onDeleteTrips: null,
};
